import React, { useEffect, useState } from "react";
import apiClient, { formatApiError } from "@/api/client";
import { Plus, X, Calendar as CalIcon, Video, BookOpen, Link2, ExternalLink, Pencil, Trash2, CheckCircle2, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";

const STATUS = {
  planlandi: { label: "Planlandı", cls: "ky-badge-indigo", dot: "ky-dot-indigo" },
  tamamlandi: { label: "Tamamlandı", cls: "ky-badge-success", dot: "ky-dot-green" },
  iptal: { label: "İptal", cls: "ky-badge-neutral", dot: "ky-dot-neutral" },
};

const EMPTY = { student_id: "", title: "", description: "", start_at: "", start_at_local: "", end_at: "", end_at_local: "", duration_minutes: 60, topic: "", attendee_ids: [], status: "planlandi" };

export default function LessonsPage() {
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState({ student_id: "", status: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.student_id) params.student_id = filter.student_id;
      if (filter.status) params.status = filter.status;
      const [{ data: ls }, { data: st }, { data: tm }] = await Promise.all([
        apiClient.get("/lessons", { params }),
        apiClient.get("/students"),
        apiClient.get("/team"),
      ]);
      setLessons(ls || []); setStudents(st || []); setTeam(tm || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter.student_id, filter.status]); // eslint-disable-line

  const openCreate = () => { setEdit(null); setForm(EMPTY); setOpen(true); };

  const openEdit = (l) => {
    setEdit(l);
    const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
    setForm({
      student_id: l.student_id || "", title: l.title || "", description: l.description || "",
      start_at: l.start_at || "", start_at_local: toLocal(l.start_at),
      end_at: l.end_at || "", end_at_local: toLocal(l.end_at),
      duration_minutes: l.duration_minutes || 60, topic: l.topic || "",
      attendee_ids: l.attendee_ids || [], status: l.status || "planlandi",
    });
    setOpen(true);
  };

  const save = async (ev) => {
    ev.preventDefault();
    try {
      const payload = { ...form };
      if (payload.start_at_local) payload.start_at = new Date(payload.start_at_local).toISOString();
      if (payload.end_at_local) payload.end_at = new Date(payload.end_at_local).toISOString();
      delete payload.start_at_local; delete payload.end_at_local;
      if (edit) await apiClient.put(`/lessons/${edit.id}`, payload);
      else await apiClient.post("/lessons", payload);
      toast.success(edit ? "Ders güncellendi" : "Ders planlandı — davetler gönderildi");
      setOpen(false); setEdit(null); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeLesson = async (id) => {
    if (!window.confirm("Dersi tamamlandı olarak işaretle?")) return;
    try { await apiClient.post(`/lessons/${id}/complete`); toast.success("Ders tamamlandı"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelLesson = async (l) => {
    if (!window.confirm("Bu ders iptal edilsin mi? (öğrencinin ilerleme sayacı 1 azalır)")) return;
    try {
      const payload = { ...l, status: "iptal" };
      // remove server-generated fields that aren't part of LessonIn
      delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.created_by; delete payload.google_event_id; delete payload.google_event_link; delete payload.meeting_url; delete payload.completed_at;
      await apiClient.put(`/lessons/${l.id}`, payload);
      toast.success("Ders iptal edildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Ders silinsin mi? (Google Calendar'dan da silinecek)")) return;
    try { await apiClient.delete(`/lessons/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const startChange = (val) => {
    const dt = new Date(val);
    if (Number.isFinite(dt.getTime())) {
      const endDt = new Date(dt.getTime() + (form.duration_minutes || 60) * 60000);
      const pad = (n) => String(n).padStart(2,'0');
      const endLocal = `${endDt.getFullYear()}-${pad(endDt.getMonth()+1)}-${pad(endDt.getDate())}T${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
      setForm(f => ({ ...f, start_at_local: val, end_at_local: endLocal }));
    } else {
      setForm(f => ({ ...f, start_at_local: val }));
    }
  };

  const sname = (sid) => students.find(s => s.id === sid)?.name || "—";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen size={22} className="text-[#7C3AED]"/>Dersler</h1>
          <p className="text-xs text-[#94A3B8] mt-1">Planla, Google Meet otomatik üretilir — öğrenci ve ekibe e-posta davet gönderilir.</p>
        </div>
        <button onClick={openCreate} className="ky-btn ky-btn-accent" data-testid="add-lesson"><Plus size={14}/>Yeni Ders</button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center bg-white rounded-lg px-3 py-2 border border-[#ECECEF]">
        <div className="w-64">
          <CustomerSelect customers={(students||[]).map(s => ({ id: s.id, name: s.name, company: s.course }))} value={filter.student_id || null} onChange={(v) => setFilter(f => ({ ...f, student_id: v || "" }))} placeholder="Tüm öğrenciler" size="sm" testid="filter-student"/>
        </div>
        <div className="flex items-center gap-1 ml-auto">
          {["", "planlandi", "tamamlandi", "iptal"].map(s => (
            <button key={s || "all"} onClick={() => setFilter(f => ({ ...f, status: s }))} className={`px-3 py-1.5 rounded-md text-xs font-medium border ${filter.status === s ? "bg-[#EEF2FF] border-[#C7D2FE] text-[#4F46E5]" : "bg-white border-[#ECECEF] text-[#64748B]"}`} data-testid={`filter-status-${s || "all"}`}>
              {s ? STATUS[s].label : "Tümü"}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[#7C3AED]"/></div>}

      <div className="space-y-2">
        {lessons.map(l => (
          <div key={l.id} className="ky-card p-4 flex items-start gap-4" data-testid={`lesson-${l.id}`}>
            <div className="w-12 h-12 rounded-lg bg-[#F3E8FF] text-[#7C3AED] flex items-center justify-center"><BookOpen size={18}/></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{l.title}</span>
                <span className={`ky-badge ${STATUS[l.status]?.cls}`}>{STATUS[l.status]?.label}</span>
                <span className="text-xs text-[#475569]">· {sname(l.student_id)}</span>
                {l.google_event_id && <span className="ky-badge ky-badge-success" title="Google Takvim'de"><Link2 size={9}/>Takvim</span>}
              </div>
              <div className="text-xs text-[#64748B] mt-1">
                {new Date(l.start_at).toLocaleString("tr-TR")}
                {l.end_at && ` → ${new Date(l.end_at).toLocaleTimeString("tr-TR",{hour:"2-digit",minute:"2-digit"})}`}
                {l.duration_minutes && ` · ${l.duration_minutes} dk`}
              </div>
              <div className="flex gap-3 mt-2 text-xs">
                {l.meeting_url && <a href={l.meeting_url} target="_blank" rel="noreferrer" className="ky-link flex items-center gap-1"><Video size={11}/>Google Meet</a>}
                {l.google_event_link && <a href={l.google_event_link} target="_blank" rel="noreferrer" className="ky-link flex items-center gap-1"><ExternalLink size={11}/>Takvim</a>}
              </div>
              {l.description && <div className="text-sm text-[#64748B] mt-2 whitespace-pre-wrap">{l.description}</div>}
            </div>
            <div className="flex flex-col gap-1.5">
              {l.status === "planlandi" && (
                <>
                  <button onClick={() => completeLesson(l.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#10B981]" title="Tamamlandı olarak işaretle" data-testid={`complete-lesson-${l.id}`}><CheckCircle2 size={13}/></button>
                  <button onClick={() => cancelLesson(l)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#F59E0B]" title="İptal et" data-testid={`cancel-lesson-${l.id}`}><Ban size={13}/></button>
                </>
              )}
              <button onClick={() => openEdit(l)} className="ky-btn ky-btn-ghost ky-btn-icon" title="Düzenle" data-testid={`edit-lesson-${l.id}`}><Pencil size={13}/></button>
              <button onClick={() => del(l.id)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]" title="Sil" data-testid={`delete-lesson-${l.id}`}><Trash2 size={13}/></button>
            </div>
          </div>
        ))}
        {!loading && lessons.length === 0 && (
          <div className="ky-card p-12 text-center">
            <BookOpen size={32} className="mx-auto text-[#CBD5E1] mb-3"/>
            <div className="text-sm font-medium">Henüz ders planlanmadı</div>
            <p className="text-xs text-[#94A3B8] mt-1">"Yeni Ders" diyerek Google Meet otomatik üretip öğrenciye davet gönderebilirsin.</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex" onClick={() => { setOpen(false); setEdit(null); }}>
          <div className="flex-1 bg-black/40"/>
          <form onSubmit={save} onClick={e => e.stopPropagation()} className="w-full sm:w-[560px] bg-white flex flex-col ky-slide-in" data-testid="lesson-form">
            <div className="px-5 py-4 border-b border-[#ECECEF] flex items-center justify-between">
              <h3 className="font-semibold">{edit ? "Ders Düzenle" : "Yeni Ders Planla"}</h3>
              <button type="button" onClick={() => { setOpen(false); setEdit(null); }} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1.5">Öğrenci *</label>
                <CustomerSelect customers={(students||[]).map(s => ({ id: s.id, name: s.name, company: s.course }))} value={form.student_id || null} onChange={(v) => setForm(f => ({ ...f, student_id: v || "" }))} placeholder="— Öğrenci seç —" required testid="lesson-student"/>
              </div>
              <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ders Başlığı *" className="ky-input font-semibold" data-testid="lesson-title"/>
              <input value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} placeholder="Konu (opsiyonel)" className="ky-input"/>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Notlar / ders içeriği" rows={3} className="ky-textarea"/>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#64748B] mb-1.5">Başlangıç *</label>
                  <input required type="datetime-local" value={form.start_at_local} onChange={e => startChange(e.target.value)} className="ky-input" data-testid="lesson-start"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#64748B] mb-1.5">Süre (dk)</label>
                  <select value={form.duration_minutes} onChange={e => {
                    const m = parseInt(e.target.value);
                    setForm(f => ({ ...f, duration_minutes: m }));
                    if (form.start_at_local) {
                      const dt = new Date(form.start_at_local);
                      const endDt = new Date(dt.getTime() + m * 60000);
                      const pad = (n) => String(n).padStart(2,'0');
                      const endLocal = `${endDt.getFullYear()}-${pad(endDt.getMonth()+1)}-${pad(endDt.getDate())}T${pad(endDt.getHours())}:${pad(endDt.getMinutes())}`;
                      setForm(f => ({ ...f, duration_minutes: m, end_at_local: endLocal }));
                    }
                  }} className="ky-input">
                    <option value={30}>30 dk</option>
                    <option value={45}>45 dk</option>
                    <option value={60}>60 dk</option>
                    <option value={90}>90 dk</option>
                    <option value={120}>120 dk</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1.5">Eğitmen / Ekip Katılımcıları (opsiyonel)</label>
                <div className="flex flex-wrap gap-1.5">
                  {team.map(t => {
                    const sel = form.attendee_ids.includes(t.id);
                    return (
                      <button key={t.id} type="button" onClick={() => setForm(f => ({ ...f, attendee_ids: sel ? f.attendee_ids.filter(i => i !== t.id) : [...f.attendee_ids, t.id] }))} className={`px-2.5 py-1 rounded-md text-xs border ${sel ? "bg-[#EEF2FF] border-[#C7D2FE] text-[#4F46E5]" : "bg-white border-[#ECECEF] text-[#64748B]"}`}>{t.name}</button>
                    );
                  })}
                </div>
              </div>
              <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-md p-3 text-xs text-[#0369A1]">
                <strong>📚 Otomatik akış:</strong> Ders kaydedilince Google Meet linki otomatik oluşur. Hem öğrenciye hem de seçili ekip üyelerine e-posta daveti gider. Tarih güncellersen yeniden bildirim atılır.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#ECECEF] flex justify-end gap-2">
              <button type="button" onClick={() => { setOpen(false); setEdit(null); }} className="ky-btn ky-btn-ghost">İptal</button>
              <button type="submit" className="ky-btn ky-btn-primary" data-testid="lesson-submit">{edit ? "Güncelle" : "Planla"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
